import React, { useRef, useEffect } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Heading from "../../components/common/Typography/Heading";
import Paragraph from "../../components/common/Typography/Paragraph";

gsap.registerPlugin(ScrollTrigger);

const STEPS = [
  { step: "Step One", title: "Create Profile" },
  { step: "Step Two", title: "Post a Job" },
  { step: "Step Three", title: "Get Contacted" },
  { step: "Step Four", title: "GET PAID" },
  { step: "Step Five", title: "Collect Reviews" },
];

const SECTION_HEIGHT_CLASS =
  "h-[1000px] sm:h-[800px] md:h-[750px] lg:h-[700px] xl:h-[800px] 2xl:h-[900px]";

const WhyTradesPoster = () => {
  const sectionRef = useRef(null);
  const pinRef = useRef(null);
  const cardVisualRef = useRef(null);
  const textViewportRef = useRef(null);
  const textTrackRef = useRef(null);
  const stepTextRefs = useRef([]);

  useEffect(() => {
    const pin = pinRef.current;
    const cardVisual = cardVisualRef.current;
    const viewport = textViewportRef.current;
    const track = textTrackRef.current;
    const stepTexts = stepTextRefs.current.filter(Boolean);
    if (!pin || !cardVisual || !viewport || !track || stepTexts.length !== STEPS.length)
      return;

    let timeline = null;
    const extraTriggers = [];

    const getStepHeight = () => viewport.offsetHeight;

    const getScrollDistance = () => getStepHeight() * (STEPS.length - 1);

    const setupScroll = () => {
      timeline?.scrollTrigger?.kill();
      timeline?.kill();
      extraTriggers.forEach((t) => t.kill());
      extraTriggers.length = 0;

      gsap.set(track, { y: 0 });
      gsap.set(cardVisual, { opacity: 0, y: 80 });
      gsap.set(stepTexts, { opacity: 0, y: 60 });
      gsap.set(stepTexts[0], { opacity: 1, y: 0 });

      extraTriggers.push(
        gsap
          .to(cardVisual, {
            opacity: 1,
            y: 0,
            duration: 1.2,
            ease: "power2.out",
            scrollTrigger: {
              trigger: pin,
              start: "top 85%",
              toggleActions: "play none none reverse",
            },
          }).scrollTrigger
      );

      timeline = gsap.timeline({
        scrollTrigger: {
          trigger: pin,
          start: "center center",
          end: () => `+=${getScrollDistance()}`,
          scrub: true,
          pin: pin,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      });

      STEPS.forEach((_, index) => {
        if (index === 0) return;

        const segment = index - 1;

        timeline.to(
          stepTexts[index - 1],
          { opacity: 0, y: -40, ease: "power2.inOut", duration: 0.35 },
          segment
        );
        timeline.to(
          stepTexts[index],
          { opacity: 1, y: 0, ease: "power2.inOut", duration: 0.35 },
          segment
        );
        timeline.to(
          track,
          { y: -getStepHeight() * index, ease: "none", duration: 1 },
          segment
        );
      });
    };

    setupScroll();

    const img = pin.querySelector("img");
    const handleRefresh = () => ScrollTrigger.refresh();
    img?.addEventListener("load", handleRefresh);
    window.addEventListener("resize", handleRefresh);
    requestAnimationFrame(handleRefresh);

    return () => {
      img?.removeEventListener("load", handleRefresh);
      window.removeEventListener("resize", handleRefresh);
      timeline?.scrollTrigger?.kill();
      timeline?.kill();
      extraTriggers.forEach((t) => t.kill());
    };
  }, []);

  return (
    <div ref={sectionRef} className="my-40 mb-0">
      <div
        ref={pinRef}
        className="h-screen w-full flex items-center justify-center"
      >
        <div
          ref={cardVisualRef}
          className={`tp-container-main mx-auto ${SECTION_HEIGHT_CLASS} relative flex justify-center items-center lg:flex-none overflow-hidden`}
        >
          <img
            src={require("../../assets/img/about-us.jpg")}
            className="w-full h-full object-cover rounded-xl bg-center bg-no-repeat"
            alt=""
          />
          <div
            ref={textViewportRef}
            className="absolute inset-0 overflow-hidden rounded-xl"
          >
            <div ref={textTrackRef} className="w-full will-change-transform">
              {STEPS.map((item, index) => (
                <div
                  key={item.step}
                  className={`${SECTION_HEIGHT_CLASS} w-full flex flex-col justify-center items-center text-center px-6 md:px-12`}
                >
                  <div
                    ref={(el) => {
                      stepTextRefs.current[index] = el;
                    }}
                    className="flex flex-col items-center text-center"
                  >
                    <Paragraph
                      variant={"MainParagraph"}
                      className="text-white mb-4 tracking-wide uppercase"
                    >
                      {item.step}
                    </Paragraph>
                    <Heading
                      variant={"h2"}
                      className="!uppercase text-white leading-tight mb-6"
                    >
                      {item.title}
                    </Heading>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhyTradesPoster;
